import React, { useCallback, useRef, useState } from 'react';
import SEO from '@components/Common/SEO/index';
import MinimalBrochureNavbar from '@src/components/brochure-view/MinimalBrochureNavbar';
import PdfViewer from '@src/components/brochure-view/PdfViewer';
import BrochureSidebar from '@src/components/brochure-view/BrochureSidebar';
import MobileBrochureSidebar from '@src/components/brochure-view/MobileBrochureSidebar';
import useBrochureAnalytics from '@src/hooks/useBrochureAnalytics';

// BrochureViewPage — the composed page rendered at /{slug}/view-brochure.
//
// Data flows in through `pageContext.brochure` (populated by gatsby-node
// createPages) rather than a local GraphQL query, so the same template
// can serve every program YAML type (FSP, DA, QA, FDT, ...).
//
// Layout:
//   - Fixed top: MinimalBrochureNavbar (Crio logo only)
//   - Desktop (lg+): PDF on the left, sticky sidebar on the right.
//   - Mobile: PDF fills the viewport; sidebar becomes a collapsible
//     bottom sheet that starts closed with just the program name + Book
//     your demo CTA, expandable to reveal bookmarks, carousel, advisor.
const BrochureViewPage = ({ pageContext }) => {
  const brochure = pageContext?.brochure || {};
  const {
    slug,
    brochurePdf,
    brochureProgramName,
    brochureBookmarks,
    seoTitle,
    seoDescription,
    canonicalUrl,
  } = brochure;

  const analytics = useBrochureAnalytics({
    programSlug: slug,
    programName: brochureProgramName,
  });

  const viewerRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(0);

  const handleJumpToPage = useCallback((page) => {
    if (viewerRef.current?.jumpToPage) viewerRef.current.jumpToPage(page);
  }, []);

  if (!brochurePdf) {
    return (
      <div className="flex min-h-[100dvh] flex-col">
        <MinimalBrochureNavbar />
        <div className="flex flex-1 items-center justify-center pt-[64px] text-center text-v5-neutral-500">
          <p>Brochure not available for this program.</p>
        </div>
      </div>
    );
  }

  const programName =
    brochureProgramName || seoTitle || 'Program Brochure';

  return (
    <div className="flex min-h-[100dvh] flex-col bg-white">
      <SEO
        title={`${programName} — Brochure`}
        description={seoDescription}
        canonicalUrl={canonicalUrl}
      />
      <MinimalBrochureNavbar />

      {/*
        Layout strategy:
        - Mobile/tablet: PDF fills the entire viewport below the nav. The
          sidebar is absolutely positioned as a bottom sheet on top of the
          PDF (see MobileBrochureSidebar).
        - Desktop (lg+): two-column grid. PDF left, sidebar right. Sidebar
          sticks to the top of the viewport for easy access while the PDF
          scrolls.
      */}
      <main className="flex flex-1 flex-col pt-[64px] lg:grid lg:grid-cols-[minmax(0,1fr)_380px] xl:grid-cols-[minmax(0,1fr)_440px]">
        <section className="relative h-[calc(100dvh-64px)] overflow-hidden">
          <PdfViewer
            ref={viewerRef}
            url={brochurePdf}
            onCurrentPageChange={setCurrentPage}
            onScrollPctChange={analytics.reportScrollDepth}
            onZoom={analytics.reportZoom}
            onDocumentLoaded={({ numPages: totalPages }) => setNumPages(totalPages)}
          />
          {/* Mobile expandable bottom sheet — only rendered below lg */}
          <div className="lg:hidden">
            <MobileBrochureSidebar
              programName={programName}
              bookmarks={brochureBookmarks || []}
              currentPage={currentPage}
              numPages={numPages}
              onJumpToPage={handleJumpToPage}
              analytics={analytics}
            />
          </div>
        </section>

        {/* Desktop sidebar — only rendered at lg+ */}
        <div className="hidden lg:sticky lg:top-[64px] lg:block lg:h-[calc(100dvh-64px)]">
          <BrochureSidebar
            programName={programName}
            bookmarks={brochureBookmarks || []}
            currentPage={currentPage}
            numPages={numPages}
            onJumpToPage={handleJumpToPage}
            analytics={analytics}
          />
        </div>
      </main>
    </div>
  );
};

export default BrochureViewPage;
